import React, { useState } from "react";
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { passwordRegex } from "../../constants";
import { useForm } from "react-hook-form";
import "date-fns";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { ChangePasswordData, updatePassword } from "../../firebase/users";
import Notification from "../shared/TemporaryNotification";
import { useAuth } from "../../firebase/AuthProvider";
import Spinner from "../shared/Spinner";
import CurrentPassword from "./CurrentPassword";
import { colors } from "../../theme";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    width: "180px",
  },
  textField: {},
  error: {
    color: colors.ERROR,
  },
}));

type StatusType = "form" | "loading" | "notifysuccess" | "notifyerror";

export default function ChangePassword() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState<StatusType>("form");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

  // Keeping the form's state and doing validation is done with react-hook-form's useForm hook.
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    triggerValidation,
  } = useForm<ChangePasswordData>();

  React.useEffect(() => {
    register(
      { name: "currentPassword" },
      {
        required: true,
      }
    );
  }, [register]);

  function handleCurrentPasswordChange(currentPassword: string) {
    setCurrentPassword(currentPassword);
    setValue("currentPassword", currentPassword);
    triggerValidation("currentPassword");
  }

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  async function save(changePasswordData: ChangePasswordData) {
    changePasswordData.email = user?.email ? user.email : "";

    setStatus("loading");

    const success = await updatePassword(changePasswordData);
    success ? setStatus("notifysuccess") : setStatus("notifyerror");

    setValue("currentPassword", "");
    setCurrentPassword("");
    setValue("newPassword", "");
  }

  return (
    <>
      <Spinner isVisible={status === "loading"} />

      {status === "notifysuccess" && (
        <Notification
          message={t("changepassword.successful")}
          handleClose={() => setStatus("form")}
          timeoutInSeconds={5}
        />
      )}

      {status === "notifyerror" && (
        <Notification
          message={t("changepassword.failed")}
          handleClose={() => setStatus("form")}
          timeoutInSeconds={5}
        />
      )}

      <form className={classes.form} noValidate onSubmit={handleSubmit(save)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CurrentPassword
              password={currentPassword}
              handleChange={handleCurrentPasswordChange}
              error={errors.currentPassword ? true : false}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl className={classes.textField} variant="outlined">
              <OutlinedInput
                id="newPassword"
                placeholder={t("changepassword.newpassword")}
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                required
                inputRef={register({
                  required: true,
                  pattern: passwordRegex,
                })}
                error={errors.newPassword ? true : false}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowNewPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={112}
              />
              {errors.newPassword && (
                <FormHelperText className={classes.error}>
                  {t("changepassword.password_valid")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
